import React from "react";
import DevilsQuadrangleChart, { axesFromDevilsQuadrangle } from "./DevilsQuadrangleChart";
import { GOAL_OPTIONS } from "./RlRedesignPanel";
import { getKpiHeadline, getHeadlineMetric } from "../../utils/nvaElimination";
import useComparisonReportData from "../../hooks/useComparisonReportData";

// The RL/Model half of the AS-IS vs TO-BE tab's engine picker. The Agent (greedy
// /cms/optimize) half is the page's own long-established rendering and is left
// untouched — this component only covers the two engines that previously had no
// presence on this tab at all.
//
// Two data sources feed one screen, fired from the SAME Run click:
//   - The KPI table (cycle time, cost) comes from the engine's own dedicated
//     endpoint (rl.result / bpr.result) — the same call the Redesign tab makes, run
//     here with whatever goal/sub-process options the analyst picks on this tab.
//   - Quality and Flexibility do not exist on either endpoint at all — only
//     GET /cms/comparison-report computes them. So Run also fires a comparison-report
//     call filtered to just this one engine (`engines=rl` or `engines=bpr`), via a
//     hook instance private to this component — NOT the page's shared
//     `useComparisonReportData()`, so an automatic fetch made for this chart never
//     overwrites whatever the analyst has set up on the actual Comparison Report tab.
//   Both calls run in parallel and render independently; the analyst never has to
//   press a second button to see quality/flexibility.
//
// RL's comparison-report run always uses RL's own default goal server-side (that
// endpoint takes no goal parameter) — so on a non-default goal, the KPI table above
// (goal-accurate) and the DQ chart below (default-goal) can legitimately diverge on
// time/cost. Said once, plainly, rather than silently plotting two different runs
// as if they were one.

const ENGINE_LABEL = { rl: "RL Agent", bpr: "BPR Model" };
const COST_HEADLINE_KEY = { rl: "resource_cost", bpr: "labor_cost_per_case" };
const COST_ROW_LABEL = { rl: "Resource cost", bpr: "Labor cost" };

function pctBadge(v) {
  if (v == null) return "—";
  const improved = v > 0;
  const color = v === 0 ? "#6b7280" : improved ? "#059669" : "#dc2626";
  const glyph = v === 0 ? "–" : improved ? "▼" : "▲";
  return <span style={{ fontWeight: 800, color }}>{glyph} {Math.abs(v).toFixed(1)}%</span>;
}

// Same 4-column shape (Dimension | AS-IS | TO-BE | Change) the Agent half of this
// tab uses, so switching the dropdown doesn't also change the table's grammar.
function KpiTable({ rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 480 }}>
        <thead>
          <tr>
            {["Dimension", "AS-IS", "TO-BE", "Change"].map((h, i) => (
              <th key={h} style={{ padding: "8px 10px", fontSize: 11, color: "var(--gray-500)", textAlign: i === 0 ? "left" : "right", borderBottom: "1px solid var(--gray-200)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={{ padding: "8px 10px", fontSize: 13, fontWeight: 600 }}>{r.label}</td>
              <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.as_is != null ? r.fmt(r.as_is) : "—"}</td>
              <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.to_be != null ? r.fmt(r.to_be) : "—"}</td>
              <td style={{ padding: "8px 10px", fontSize: 13, textAlign: "right" }}>{pctBadge(r.reduction_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 4, fontSize: 10.5, color: "var(--gray-400)" }}>
        Positive change = the redesign improved this dimension, matching the Redesign tab.
      </div>
    </div>
  );
}

function DqSection({ engine, dq, onRetry }) {
  const cmpEngine = (dq.result?.engines || []).find((e) => e.engine === engine);
  const axes = axesFromDevilsQuadrangle(cmpEngine?.devils_quadrangle);

  if (dq.running) {
    return (
      <div style={{ marginTop: 16, padding: "12px 16px", border: "1px solid var(--slate-100)", borderRadius: 10, fontSize: 12.5, color: "var(--gray-500)" }}>
        Computing Quality &amp; Flexibility…
      </div>
    );
  }

  if (axes) {
    return (
      <div style={{ marginTop: 16, border: "1px solid var(--slate-100)", borderRadius: 10, padding: "12px 16px", background: "var(--gray-50)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gray-500)", marginBottom: 4 }}>
          DEVIL&apos;S QUADRANGLE
        </div>
        <DevilsQuadrangleChart axes={axes} />
        {/* Quality/Flexibility exist only via the comparison-report computation, which
            always runs RL on its own default goal — a custom goal picked above only
            reaches the KPI table, not this chart. */}
        <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--gray-400)" }}>
          Quality and Flexibility come from the same comparison computation used on the Comparison
          Report tab, which runs {ENGINE_LABEL[engine]} on its own default goal — they may not
          reflect the goal selected above. Time and cost above are from the run above instead, and do
          honour it.
        </div>
      </div>
    );
  }

  if (dq.runError) {
    return (
      <div style={{ marginTop: 16, padding: "10px 14px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 10, fontSize: 12.5, color: "var(--red-ink-800)" }}>
        Couldn&apos;t compute Quality &amp; Flexibility: {dq.runError}{" "}
        <button type="button" className="secondary-btn sm" style={{ marginLeft: 6 }} onClick={onRetry}>Retry</button>
      </div>
    );
  }

  return null;
}

function RlSection({ processId, money, fmtMin, rl, goal, setGoal, includeSubprocess, setIncludeSubprocess, dq }) {
  const headline = getKpiHeadline(rl.result);
  const timeM = getHeadlineMetric(headline, "cycle_time_minutes");
  const costM = getHeadlineMetric(headline, COST_HEADLINE_KEY.rl);

  const runBoth = () => {
    rl.run(processId, goal, { includeSubprocessRedesigns: includeSubprocess });
    dq.run(processId, { engines: ["rl"], includeSubprocessRedesigns: includeSubprocess });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Goal</span>
        {GOAL_OPTIONS.map((g) => (
          <button
            key={g.key}
            type="button"
            className={`opt-tab-btn ${goal === g.key ? "active" : ""}`}
            style={{ padding: "4px 14px", fontSize: 12 }}
            title={g.hint}
            onClick={() => setGoal(g.key)}
          >
            {g.label}
          </button>
        ))}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, fontSize: 12, color: "var(--gray-700)", cursor: "pointer" }}>
        <input type="checkbox" checked={includeSubprocess} disabled={rl.running} onChange={(e) => setIncludeSubprocess(e.target.checked)} />
        <span>Also redesign this process&apos;s sub-processes</span>
      </label>
      <button type="button" className="primary-btn sm" disabled={rl.running} style={{ marginBottom: 14 }} onClick={runBoth}>
        {rl.running ? "Training the RL agent…" : rl.result ? "Re-run" : "Run RL"}
      </button>

      {rl.runError && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 10, color: "var(--red-ink-800)", fontSize: 13 }}>
          {rl.runError}
        </div>
      )}

      {!rl.result && !rl.running && !rl.runError && (
        <div className="no-results">Pick a goal and run RL to see its AS-IS vs TO-BE here.</div>
      )}

      {rl.result && (
        <>
          <KpiTable
            rows={[
              { label: "Cycle time (min)", as_is: timeM?.as_is, to_be: timeM?.to_be, reduction_pct: timeM?.reduction_pct, fmt: fmtMin },
              { label: COST_ROW_LABEL.rl, as_is: costM?.as_is, to_be: costM?.to_be, reduction_pct: costM?.reduction_pct, fmt: money },
            ]}
          />
          <DqSection engine="rl" dq={dq} onRetry={runBoth} />
        </>
      )}
    </div>
  );
}

function BprSection({ processId, money, fmtMin, bpr, includeSubprocess, setIncludeSubprocess, dq }) {
  const headline = getKpiHeadline(bpr.result);
  const timeM = getHeadlineMetric(headline, "cycle_time_minutes");
  const costM = getHeadlineMetric(headline, COST_HEADLINE_KEY.bpr);

  const runBoth = () => {
    bpr.run(processId, { includeSubprocessRedesigns: includeSubprocess });
    dq.run(processId, { engines: ["bpr"], includeSubprocessRedesigns: includeSubprocess });
  };

  return (
    <div>
      <label style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12, fontSize: 12, color: "var(--gray-700)", cursor: "pointer" }}>
        <input type="checkbox" checked={includeSubprocess} disabled={bpr.running} onChange={(e) => setIncludeSubprocess(e.target.checked)} />
        <span>Also redesign this process&apos;s sub-processes</span>
      </label>
      <button type="button" className="primary-btn sm" disabled={bpr.running} style={{ marginBottom: 14 }} onClick={runBoth}>
        {bpr.running ? "Redesigning…" : bpr.result ? "Re-run" : "Run BPR Model"}
      </button>

      {bpr.runError && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 10, color: "var(--red-ink-800)", fontSize: 13 }}>
          {bpr.runError}
        </div>
      )}

      {!bpr.result && !bpr.running && !bpr.runError && (
        <div className="no-results">Run the BPR model to see its AS-IS vs TO-BE here.</div>
      )}

      {bpr.result && (
        <>
          <KpiTable
            rows={[
              { label: "Cycle time (min)", as_is: timeM?.as_is, to_be: timeM?.to_be, reduction_pct: timeM?.reduction_pct, fmt: fmtMin },
              { label: COST_ROW_LABEL.bpr, as_is: costM?.as_is, to_be: costM?.to_be, reduction_pct: costM?.reduction_pct, fmt: money },
            ]}
          />
          <DqSection engine="bpr" dq={dq} onRetry={runBoth} />
        </>
      )}
    </div>
  );
}

export default function BeforeAfterEnginePanel({
  engine, processId, money, fmtMin,
  rl, bpr,
  goal, setGoal, includeSubprocess, setIncludeSubprocess,
}) {
  // Private to this component, deliberately not the page's shared comparison-report
  // state — see the file-level comment for why.
  const dq = useComparisonReportData();

  if (engine === "rl") {
    return (
      <RlSection
        processId={processId} fmtMin={fmtMin} money={money} rl={rl}
        goal={goal} setGoal={setGoal}
        includeSubprocess={includeSubprocess} setIncludeSubprocess={setIncludeSubprocess}
        dq={dq}
      />
    );
  }
  return (
    <BprSection
      processId={processId} fmtMin={fmtMin} money={money} bpr={bpr}
      includeSubprocess={includeSubprocess} setIncludeSubprocess={setIncludeSubprocess}
      dq={dq}
    />
  );
}
